<template>
  <AccountContextWrapper>
    <div v-if="acctUid !== null" class="row">
      <div class="col-12">
        <div class="row">
          <div class="col-6">
            <BFormGroup
              id="input-group-filter-start-date"
              label="Outlooks Start Date:"
              label-for="filter-start-date"
              class="floating-group"
            >
              <BFormInput
                id="filter-start-date"
                v-model="weeklyOutlookStartDate"
                type="date"
                size="sm"
                style="font-size: 0.85rem"
                :class="{ 'is-invalid': fltrsDateError !== null }"
                @change="onDateChange"
              />
            </BFormGroup>
          </div>
          <div class="col-6">
            <BFormGroup
              id="input-group-filter-end-date"
              label="Outlooks End Date:"
              label-for="filter-end-date"
              class="floating-group"
            >
              <BFormInput
                id="filter-end-date"
                v-model="weeklyOutlookEndDate"
                type="date"
                size="sm"
                style="font-size: 0.85rem"
                :class="{ 'is-invalid': fltrsDateError !== null }"
                @change="onDateChange"
              />
            </BFormGroup>
          </div>
        </div>
      </div>
      <div class="col-12 text-center">
        <span v-if="fltrsDateError" class="small text-danger">{{
          fltrsDateError
        }}</span>
      </div>
      <div class="col-12">
        <div class="row">
          <div class="col-12">
            <BFormGroup
              id="input-group-weekly-outlook-filter"
              label="Selected Weekly Outlook:"
              label-for="weekly-outlook-filter"
              class="floating-group"
            >
              <BFormSelect
                id="weekly-outlook-filter"
                :modelValue="selectedWeeklyOutlook?.weekly_outlook"
                :options="weeklyOutlooksAsOpts"
                size="sm"
                style="font-size: 0.85rem"
                @update:modelValue="handleWeeklyOutlookSelection"
              />
            </BFormGroup>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div
              class="d-flex flex-no-wrap justify-content-center align-items-center px-3 py-2 border-bottom bg-white gap-1"
            >
              <BFormGroup
                id="input-group-weekly-outlook-budget"
                label="Week's Budget:"
                label-for="weekly-outlook-budget"
                class="floating-group"
                style="max-width: 125px"
              >
                <BFormInput
                  id="weekly-outlook-budget"
                  :modelValue="selectedWeeklyOutlook?.budget"
                  type="number"
                  step="0.01"
                  size="sm"
                  style="font-size: 0.85rem"
                  readonly
                />
              </BFormGroup>
              <BFormGroup
                id="input-group-weekly-outlook-leftover"
                label="Week's Leftover:"
                label-for="weekly-outlook-leftover"
                class="floating-group"
                style="max-width: 125px"
              >
                <BFormInput
                  id="weekly-outlook-leftover"
                  :modelValue="selectedWeeklyOutlookLeftover"
                  :class="weeksLeftOverInputClass"
                  type="number"
                  step="0.01"
                  size="sm"
                  style="font-size: 0.85rem"
                  readonly
                />
              </BFormGroup>
              <IconRefresh
                size="16"
                class="text-success ms-2"
                style="cursor: pointer"
                @click="fetchSelectedWeeklyOutlookTransactions"
              />
              <IconMoneybagPlus
                size="16"
                class="text-primary ms-2"
                style="cursor: pointer"
                @click="createModal = true"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center">
        <ListView
          v-if="weeklyOutlookTransactions !== null"
          :loadingAssignments="loadingAssignments"
          :loadingSubmittedTransactions="loadingSubmittedTransactions"
          :assignments="weeklyOutlookTransactions"
          :submittedTransactions="submittedTransactions"
          :acctUid="acctUid"
        />
      </div>
    </div>
  </AccountContextWrapper>
  <CreateAssignmentModal
    v-if="acctUid && selectedWeeklyOutlook"
    v-model="createModal"
    :acctUid="acctUid"
    :weeklyOutlookValue="selectedWeeklyOutlook?.weekly_outlook"
    @assignmentCreated="fetchWeeklyOutlookTransactions"
  />
</template>
<script>
import { BFormInput, BFormGroup, BFormSelect } from "bootstrap-vue-next";
import ListView from "@/components/WeeklyOutlooks/ListView.vue";
import CreateAssignmentModal from "@/components/WeeklyOutlooks/CreateAssignmentModal.vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import {
  userAccount as userAcctNs,
  weeklyOutlooks as weeklyOutlooksNs,
  spendingCategory as spendingCategoryNs,
  bankingInstitutions as bankingInstitutionsNs,
} from "@/store/namespaces";
import { formatDateToyyyyMMdd } from "@/utils/utils.js";
import { IconRefresh, IconMoneybagPlus } from "@tabler/icons-vue";
import { getThisWeeksMonday } from "@/utils/utils";

export default {
  name: "WeeklyOutlooksIndexView",
  components: {
    AccountContextWrapper,
    BFormInput,
    BFormGroup,
    ListView,
    IconRefresh,
    BFormSelect,
    IconMoneybagPlus,
    CreateAssignmentModal,
  },
  data() {
    return {
      createModal: false,
      weeklyOutlookStartDate: null,
      weeklyOutlookEndDate: null,
      thisMonday: null,
      selectedWeeklyOutlook: null,
      submittedTransactions: [],
      fltrsDateError: null,
      loadingAssignments: false, // Weekly outlook assignments
      loadingSubmittedTransactions: false,
    };
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    weeklyOutlooks() {
      return this.$store.getters[`${weeklyOutlooksNs}/getWeeklyOutlooks`];
    },
    trsxnsByWeeklyOutlook() {
      return this.$store.getters[
        `${weeklyOutlooksNs}/getTransactionsByWeeklyOutlook`
      ];
    },
    weeklyOutlookTransactions() {
      if (!this.selectedWeeklyOutlook) return [];
      return (
        this.trsxnsByWeeklyOutlook?.[
          this.selectedWeeklyOutlook.weekly_outlook
        ] || []
      );
    },
    selectedWeeklyOutlookTransactionStartDate() {
      return this.selectedWeeklyOutlook?.weekly_outlook || null;
    },
    selectedWeeklyOutlookTransactionEndDate() {
      if (!this.selectedWeeklyOutlookTransactionStartDate) return null;

      const endDateObj = new Date(
        `${this.selectedWeeklyOutlookTransactionStartDate}T00:00:00`
      );
      endDateObj.setDate(endDateObj.getDate() + 6);
      return formatDateToyyyyMMdd(endDateObj, "-");
    },
    weeklyOutlooksAsOpts() {
      if (!this.weeklyOutlooks) return [];

      return [
        { value: null, text: "— Select a week —" },
        ...this.weeklyOutlooks.map((week) => ({
          value: week.weekly_outlook,
          text:
            week.weekly_outlook === this.thisMonday
              ? `${week.weekly_outlook} (This Week)`
              : week.weekly_outlook,
        })),
      ];
    },
    selectedWeeklyOutlookLeftover() {
      if (!this.selectedWeeklyOutlook) return null;
      const assignedTxns = this.weeklyOutlookTransactions || [];
      const totalAssigned = assignedTxns.reduce(
        (sum, txn) => sum + txn.amount,
        0
      );
      return (this.selectedWeeklyOutlook.budget + totalAssigned).toFixed(2);
    },
    weeksLeftOverInputClass() {
      const leftover = parseFloat(this.selectedWeeklyOutlookLeftover);
      if (isNaN(leftover)) return "";
      if (leftover < 50) {
        return "text-danger";
      } else if (leftover < 100) {
        return "text-warning";
      } else {
        return "";
      }
    },
  },
  watch: {
    // Make sure to refetch when the selected account changes
    acctUid(newAcctUid, oldAcctUid) {
      if (newAcctUid !== oldAcctUid) {
        // this.fetchTransactionsByDateRange(); // Removing this for now
        this.initialize();
      }
    },
  },
  mounted() {
    if (!this.acctUid) return;
    this.initialize();
  },
  methods: {
    validateDateFilters() {
      if (!this.weeklyOutlookStartDate) {
        this.fltrsDateError = "Start date is required.";
        return false;
      }

      if (!this.weeklyOutlookEndDate) {
        this.fltrsDateError = "End date is required.";
        return false;
      }

      const start = new Date(this.weeklyOutlookStartDate);
      const end = new Date(this.weeklyOutlookEndDate);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffDays < 0) {
        this.fltrsDateError = "End date cannot be before start date.";
        return false;
      }

      if (diffDays > 93) {
        this.fltrsDateError = "Date range cannot exceed 93 days.";
        return false;
      }

      this.fltrsDateError = null;
      return true;
    },
    async initialize(force = false) {
      // Fetch spending categories on mount to ensure they are available for transaction assignment
      // Reminder: this method already handles caching internally, so we can call it without checking if we already have categories
      let params = { sort: "name:asc" };
      this.$store.dispatch(
        `${spendingCategoryNs}/fetchSpendingCategories`,
        params
      );

      this.thisMonday = await getThisWeeksMonday();
      if (!this.weeklyOutlookStartDate || !this.weeklyOutlookEndDate) {
        await this.initializeDateFilters();
      }
      await this.fetchWeeklyOutlooks(force);
      this.fetchBankingInstitutions();

      this.handleWeeklyOutlookSelection(this.thisMonday, {
        forceReload: true,
      });
    },
    // Important so we don't load ALL weekly outlooks for the account
    async initializeDateFilters() {
      const today = new Date();
      const startDateObj = new Date(today);
      startDateObj.setDate(startDateObj.getDate() - 45);
      this.weeklyOutlookStartDate = formatDateToyyyyMMdd(startDateObj, "-");

      const endDateObj = new Date(today);
      endDateObj.setDate(endDateObj.getDate() + 45);
      this.weeklyOutlookEndDate = formatDateToyyyyMMdd(endDateObj, "-");
    },
    async fetchWeeklyOutlooks(force = false) {
      if (!this.validateDateFilters()) return;

      const params = {
        filter: [
          `acctUid:eq:${this.acctUid}`,
          `weeklyOutlook:gte:${this.weeklyOutlookStartDate}`,
          `weeklyOutlook:lte:${this.weeklyOutlookEndDate}`,
        ],
      };
      await this.$store.dispatch(`${weeklyOutlooksNs}/fetchWeeklyOutlooks`, {
        qryParams: params,
        force,
      });
    },
    fetchBankingInstitutions() {
      const params = {
        filter: [`acctUid:eq:${this.acctUid}`],
      };
      this.$store.dispatch(
        `${bankingInstitutionsNs}/fetchBankingInstitutions`,
        {
          qryParams: params,
        }
      );
    },
    onDateChange() {
      this.fltrsDateError = null;
      this.initialize(true);
    },
    async fetchSelectedWeeklyOutlookTransactions() {
      await Promise.all([
        this.fetchWeeklyOutlookTransactions(),
        this.fetchSubmittedTransactions(),
      ]);
    },
    async fetchWeeklyOutlookTransactions() {
      if (!this.selectedWeeklyOutlook) {
        console.warn(
          "Cannot fetch assignments for weekly outlook because no weekly outlook is selected."
        );
        return;
      }

      try {
        this.loadingAssignments = true;
        const params = {
          filter: [
            `acctUid:eq:${this.acctUid}`,
            `weeklyOutlook:eq:${this.selectedWeeklyOutlook.weekly_outlook}`,
          ],
        };
        await this.$store.dispatch(
          `${weeklyOutlooksNs}/fetchTransactionsForWeeklyOutlook`,
          {
            qryParams: params,
            weeklyOutlookId: this.selectedWeeklyOutlook.weekly_outlook,
          }
        );
      } catch (error) {
        console.error("Error fetching assignments for weekly outlook:", error);
      } finally {
        this.loadingAssignments = false;
      }
    },
    async fetchSubmittedTransactions() {
      if (!this.selectedWeeklyOutlook) {
        console.warn(
          "Cannot fetch submitted transactions because no weekly outlook is selected."
        );
        this.submittedTransactions = [];
        return;
      }

      if (!this.acctUid) {
        console.warn(
          "Cannot fetch submitted transactions because no account is selected."
        );
        this.submittedTransactions = [];
        return;
      }

      this.loadingSubmittedTransactions = true;
      try {
        const response = await this.$api.get(
          "/banking-institutions/transactions",
          {
            params: {
              filter: [
                `acctUid:eq:${this.acctUid}`,
                `date:gte:${this.selectedWeeklyOutlookTransactionStartDate}`,
                `date:lte:${this.selectedWeeklyOutlookTransactionEndDate}`,
              ],
            },
          }
        );
        this.submittedTransactions = response.data.map((txn) => txn.item);
      } catch (error) {
        console.error("Error fetching submitted transactions:", error);
        this.submittedTransactions = [];
      } finally {
        this.loadingSubmittedTransactions = false;
      }
    },
    handleWeeklyOutlookSelection(wo, { forceReload = false } = {}) {
      const currentWeeklyOutlookValue =
        this.selectedWeeklyOutlook?.weekly_outlook || null;
      const nextWeeklyOutlook =
        this.weeklyOutlooks?.find(
          (weeklyOutlook) => weeklyOutlook.weekly_outlook === wo
        ) || null;

      if (!nextWeeklyOutlook) {
        this.selectedWeeklyOutlook = null;
        this.submittedTransactions = [];
        return;
      }

      const hasSameSelection =
        nextWeeklyOutlook.weekly_outlook === currentWeeklyOutlookValue;

      this.selectedWeeklyOutlook = nextWeeklyOutlook;

      if (hasSameSelection && !forceReload) {
        return;
      }

      this.fetchSelectedWeeklyOutlookTransactions();
    },
  },
};
</script>
